import React from "react";

import { useKeyboardListNavigation } from "@agent-ui/features/chat/components/chat-elements/context-items-menu/useKeyboardListNavigation";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { ImageIcon, Search } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";

interface IntegrationObjectResult {
  id: string;
  title?: string;
  handle?: string;
  imageUrl?: string | null;
  subtitle?: string;
}

const ObjectItemSkeleton: React.FC = () => {
  return (
    <div className="flex flex-row gap-1 px-2 py-1.5 items-center rounded-lg w-full">
      <Skeleton className="h-8 w-8 min-h-8 min-w-8 flex-shrink-0 rounded-md" />
      <div className="flex flex-col w-full pl-1 gap-1">
        <Skeleton className="h-3 w-21" />
        <Skeleton className="h-3 w-52" />
      </div>
    </div>
  );
};

const ObjectContextItem = React.forwardRef<
  HTMLButtonElement,
  {
    result: IntegrationObjectResult;
    onSelect: (result: IntegrationObjectResult) => void;
    isFocused?: boolean;
  }
>(({ result, onSelect, isFocused = false }, ref) => {
  return (
    <button
      ref={ref}
      className={twMerge(
        "flex flex-row gap-1 p-1.5 py-1 items-center rounded-lg transition-colors cursor-pointer w-full text-left",
        "border border-transparent hover:bg-menu-item-selected/50 focus:outline-none focus:ring-2 focus:ring-primary/50",
        isFocused && "bg-menu-item-selected",
      )}
      onClick={() => {
        onSelect(result);
      }}
    >
      {result.imageUrl ? (
        <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md overflow-hidden">
          <img
            src={result.imageUrl}
            alt={result.title ?? ""}
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md">
          <ImageIcon className="text-muted size-4" />
        </div>
      )}
      <div className="flex flex-col w-full pl-1 min-w-0">
        <div className="text-xs font-medium truncate" title={result.title}>
          {result.title ?? result.id}
        </div>
        {result.subtitle && (
          <span className="text-xs text-muted m-0 flex-1 min-w-0 truncate">
            {result.subtitle}
          </span>
        )}
      </div>
    </button>
  );
});

ObjectContextItem.displayName = "ObjectContextItem";

interface IntegrationObjectContextItemsListProps {
  results: IntegrationObjectResult[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  onSelect: (result: IntegrationObjectResult) => void;
  searchQuery: string;
  scrollableTargetId: string;
  listContainerId: string;
  emptyTitle: string;
  emptySearchTitle: string;
}

export const IntegrationObjectContextItemsList: React.FC<
  IntegrationObjectContextItemsListProps
> = ({
  results,
  isLoading,
  searchQuery,
  fetchNextPage,
  hasNextPage,
  onSelect,
  scrollableTargetId,
  listContainerId,
  emptyTitle,
  emptySearchTitle,
}) => {
  const { focusedIndex, handleKeyDown, getItemRef } = useKeyboardListNavigation(
    {
      itemCount: results.length,
      onSelect: (index) => {
        if (results[index]) {
          onSelect(results[index]);
        }
      },
      isEnabled: !isLoading && results.length > 0,
    },
  );

  const noResults =
    results.length === 0 && !isLoading && searchQuery.length === 0;
  const emptySearch =
    results.length === 0 && !isLoading && searchQuery.length > 0;

  if (isLoading) {
    return (
      <div className="flex flex-col flex-1">
        <div className="flex-1 overflow-y-auto p-2">
          <div className="flex flex-col gap-1">
            {Array.from({ length: 5 }).map((_, index) => (
              <ObjectItemSkeleton key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (noResults) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden h-full pb-3">
        <div className="flex-1 p-2 flex items-center justify-center">
          <Empty
            title={emptyTitle}
            description="Try searching to find items."
            size="sm"
            icon={<Search />}
          />
        </div>
      </div>
    );
  }

  if (emptySearch) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden h-full pb-3">
        <div className="flex-1 p-2 flex items-center justify-center">
          <Empty
            title={emptySearchTitle}
            description="Try adjusting your search."
            size="sm"
            icon={<Search />}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col flex-1"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      data-list-container={listContainerId}
    >
      <InfiniteScroll
        dataLength={results.length}
        next={fetchNextPage}
        hasMore={hasNextPage}
        loader={
          <div className="px-2">
            <ObjectItemSkeleton />
          </div>
        }
        scrollableTarget={scrollableTargetId}
      >
        <div className="flex flex-col gap-1 p-2 pb-1">
          {results.map((result, index) => {
            return (
              <ObjectContextItem
                key={result.id}
                ref={getItemRef(index)}
                result={result}
                onSelect={onSelect}
                isFocused={index === focusedIndex}
              />
            );
          })}
        </div>
      </InfiniteScroll>
    </div>
  );
};
